import { computed, ref } from "vue";
import { trackTab } from "@/analytics/drawing-analytics";
import DraggerOptionState from "@/stores/DraggerOptionState";
import DraggerMenuState from "@/stores/DraggerMenuState";
import TemplateBlocksState from "@/stores/TemplateBlocksState";
import { DraggerType, Facade } from "@/drawing";
import type { BlockAttributeDefInfo } from "@/drawing/blocks";

/**
 * Choix d'un bloc à poser (onglet Objets) : arme l'outil Bloc, en demandant
 * d'abord les valeurs d'attributs quand la définition en porte.
 */
export function useBlockPicking() {
  const activeName = computed(() => DraggerOptionState.activeBlock?.name);
  const isBlockDraggerActive = computed(
    () => DraggerMenuState.activeDragger.name == DraggerType.Block,
  );

  /** Bloc armé (surligné dans les listes). */
  function isArmed(name: string): boolean {
    return name === activeName.value && isBlockDraggerActive.value;
  }

  /** Block awaiting its attribute values in the pre-insert form. */
  const attributesPrompt = ref<{
    blockName: string;
    attributes: BlockAttributeDefInfo[];
    initialValues: Record<string, string> | null;
  } | null>(null);
  const showAttributesModal = ref(false);

  function armBlockDragger(
    name: string,
    attributeValues: Record<string, string> | null,
  ) {
    DraggerMenuState.setActiveDragger({ name: DraggerType.Block }, "blocks");
    DraggerOptionState.setActiveBlock(name);
    if (Facade.isInitialized) {
      Facade.blocks.setActiveBlockAttributeValues(attributeValues);
    }
  }

  function openAttributesModal(
    name: string,
    attributes: BlockAttributeDefInfo[],
  ) {
    // Prefill with what was entered when this block is the armed one — the
    // values live on the dragger and are cleared whenever the active block
    // changes.
    attributesPrompt.value = {
      blockName: name,
      attributes,
      initialValues:
        isArmed(name) && Facade.isInitialized
          ? Facade.blocks.getActiveBlockAttributeValues()
          : null,
    };
    showAttributesModal.value = true;
  }

  /**
   * Arm the block tool, asking for attribute values first when the definition
   * carries editable ATTDEFs. The values apply to every insert until the tool
   * changes; each placed instance stays editable via the attributes panel.
   * Re-selecting the armed block reopens the form prefilled with the values
   * already entered, so they can be adjusted between inserts.
   * The definition must already be in the drawing database (imported).
   */
  function armWithAttributesPrompt(name: string) {
    const attributes = Facade.isInitialized
      ? Facade.blocks.listBlockDefinitionAttributes(name)
      : [];
    if (attributes.length) {
      openAttributesModal(name, attributes);
    } else {
      armBlockDragger(name, null);
    }
  }

  function onAttributesConfirm(values: Record<string, string>) {
    const prompt = attributesPrompt.value;
    if (prompt) armBlockDragger(prompt.blockName, values);
  }

  function selectDRBlock(name: string) {
    trackTab("blocks", "block_picked", { catalog: "dr" });
    DraggerOptionState.importDRBlock(name);
    armWithAttributesPrompt(name);
  }

  function selectDwgBlock(name: string) {
    trackTab("blocks", "block_picked", { catalog: "drawing" });
    armWithAttributesPrompt(name);
  }

  function selectLegendBlock(name: string) {
    trackTab("blocks", "block_picked", { catalog: "legend" });
    const ok = TemplateBlocksState.importBlock(name);
    if (ok) armWithAttributesPrompt(name);
  }

  return {
    isArmed,
    attributesPrompt,
    showAttributesModal,
    onAttributesConfirm,
    selectDRBlock,
    selectDwgBlock,
    selectLegendBlock,
  };
}
